// Totales del packing list y correcciones manuales de línea.
//
// Los totales se calculan con LAS MISMAS fórmulas que DocGen (docs/API.md §4.2),
// que los recalcula por su cuenta y no se le mandan. Si estas divergen, el
// usuario revisa unas cifras en el cajón y el cliente recibe otras en el PDF.
//
//   Uds. totales = unidades  ||  cantidadCajas × unidadesCaja
//   Neto total   = pesoCaja      × cantidadCajas
//   Bruto total  = pesoBrutoCaja × cantidadCajas
//   Nº Palets    = recuento de matriculaPalet DISTINTAS (en el estimado
//                  paletizado, la estimación declarada: `paletsEstimados`)
//
// Las dos primeras valen igual con `noPaletizado` 0 y 1: el contrato unificado ya
// no tiene pesos por palet. Y los palets se CUENTAN, no se suman: un palet mixto
// con dos referencias son dos líneas y un solo palet.
import type { PackingListCabecera, PackingListLinea, TipoPaletizado } from '@/types'

/** Separador de las claves compuestas.
 *
 *  Es el caracter NUL: ningun codigo de articulo, matricula ni lote lo
 *  contiene, asi que dos claves distintas no pueden colisionar (un espacio
 *  si podria, si algun dia un codigo llevara uno).
 *
 *  Se construye con `fromCharCode` y no se escribe ni crudo ni como escape:
 *  un NUL literal en el fuente hace que git marque el fichero como binario y
 *  se quede sin diff ni blame. Esto es ASCII plano. */
const SEP = String.fromCharCode(0)

export interface TotalesPacking {
  cajas: number
  palets: number
  unidades: number
  neto: number
  bruto: number
}

/** Uds. totales de una línea: lo que diga el origen, o cajas × uds/caja. */
export function unidadesLinea(l: PackingListLinea): number {
  if (l.unidades) return l.unidades
  return (l.cantidadCajas ?? 0) * (l.unidadesCaja ?? 0)
}

/** Neto y bruto de una línea. Siempre por caja, en los dos modos. */
export function pesosLinea(l: PackingListLinea) {
  const cajas = l.cantidadCajas ?? 0
  return {
    neto: (l.pesoCaja ?? 0) * cajas,
    bruto: (l.pesoBrutoCaja ?? 0) * cajas,
  }
}

/** Cuántos palets declara el documento: matrículas DISTINTAS.
 *
 *  Nunca la suma de un contador por línea. Con un palet mixto de dos referencias,
 *  sumar daría 2 donde hay 1. */
export function paletsDistintos(lineas: PackingListLinea[]): number {
  return new Set(lineas.map(l => l.matriculaPalet).filter(Boolean)).size
}

/** La fila TOTALES tal como saldrá en el PDF. */
export function totalesPacking(lineas: PackingListLinea[]): TotalesPacking {
  const acc = lineas.reduce<TotalesPacking>(
    (a, l) => {
      const { neto, bruto } = pesosLinea(l)
      a.cajas += l.cantidadCajas ?? 0
      a.unidades += unidadesLinea(l)
      a.neto += neto
      a.bruto += bruto
      return a
    },
    { cajas: 0, palets: 0, unidades: 0, neto: 0, bruto: 0 },
  )
  acc.palets = paletsDistintos(lineas)
  return acc
}

/** Los palets que declara el packing AVP estimado paletizado.
 *
 *  El estimado no cuenta matrículas (no hay carga montada): declara la
 *  estimación del TIPO elegido, mixto o mono, y su tara se suma al bruto. La
 *  cuenta es la misma que hace el backend al generar, para que la pantalla
 *  enseñe el bruto que saldrá en el PDF. `null` si la carga no va por esta vía
 *  o si el tipo no tiene recuento (el backend saca entonces el no paletizado).
 */
export function paletsEstimados(
  c: PackingListCabecera,
  tipo: TipoPaletizado | null | undefined,
): { tipo: TipoPaletizado; palets: number; tara: number | null } | null {
  if (!c.estimadoPaletizado) return null
  const t: TipoPaletizado = tipo ?? c.tipoPaletizado ?? 'mono'
  const palets = t === 'mixto' ? c.paletsEstimadosMixto : c.paletsEstimadosMono
  if (!palets) return null
  return { tipo: t, palets, tara: (t === 'mixto' ? c.pesoPaletsMixto : c.pesoPaletsMono) ?? null }
}

// ── Correcciones manuales de línea ───────────────────────────────────────────
//
// Los datos maestros de D365 fallan: un artículo sin peso, unas uds/caja
// obsoletas. El usuario tiene que poder corregirlo antes de generar, y para eso
// necesita ver el efecto mientras escribe — de ahí que la cascada viva aquí, en
// el cliente, y no en el backend.
//
// El backend aplica las correcciones tal cual llegan y registra el antes→después
// de cada campo en auditoría. Es la única parte del endpoint donde el navegador
// decide el contenido del documento, y ese rastro es lo que la hace defendible.

/** Campos corregibles. Son exactamente los que el PDF imprime; las columnas de
 *  control quedan fuera porque explican de dónde sale el dato y editarlas haría
 *  que la explicación mintiera. */
export const CAMPOS_EDITABLES = [
  'descripcion', 'hscode', 'unidades', 'unidadesCaja', 'cantidadCajas',
  'pesoCaja', 'pesoBrutoCaja',
] as const

export type CampoEditable = typeof CAMPOS_EDITABLES[number]

/** Lo único que se CALCULA a partir de otros campos.
 *
 *  Con el contrato nuevo la cascada es una sola fórmula: al desaparecer los pesos
 *  por palet, ya no hay cadena de derivados que mantener en pie. */
export const CAMPOS_DERIVADOS: CampoEditable[] = ['cantidadCajas']

/** Identidad de una línea: **(matrícula, artículo)**.
 *
 *  El artículo solo no vale: con el grano nuevo aparece en tantas líneas como
 *  palets ocupe, y corregir por artículo aplicaría el cambio a todas a la vez. */
export function claveLinea(l: { matriculaPalet?: string | null; articulo: string }): string {
  return `${l.matriculaPalet ?? ''}${SEP}${l.articulo}`
}

/** Lo que el usuario ha cambiado en una línea. */
export interface CorreccionLinea {
  /** Valores tecleados. Solo los campos realmente tocados. */
  valores: Partial<Record<CampoEditable, string | number>>
  /** Campos que NO se recalculan. Editar una celda la fija automáticamente:
   *  «lo que escribo se queda». Se puede soltar el candado. */
  fijados: CampoEditable[]
  excluida: boolean
}

const dec2 = (n: number) => Math.round(n * 100) / 100

/** ¿Hay algo que aplicar? Una corrección recién creada está vacía. */
export function correccionVacia(c: CorreccionLinea | undefined): boolean {
  if (!c) return true
  return !c.excluida && Object.keys(c.valores).length === 0
}

/** La línea con la corrección aplicada y la cascada resuelta.
 *
 *  Sin corrección devuelve la línea TAL CUAL, sin pasar por la cascada. Es
 *  deliberado: recalcular una línea intacta la movería un céntimo por el redondeo
 *  de los valores intermedios, y abrir el cajón no puede cambiar cifras que nadie
 *  ha tocado. */
export function aplicarCorreccion(
  original: PackingListLinea,
  correccion: CorreccionLinea | undefined,
): PackingListLinea {
  if (correccionVacia(correccion)) return original

  const fijados = new Set(correccion!.fijados)
  const l: PackingListLinea = { ...original }

  // 1. Lo tecleado, convertido. Un campo vaciado vuelve a «sin dato» (null), que
  //    no es lo mismo que cero: el PDF lo deja en blanco en vez de mentir.
  for (const [campo, valor] of Object.entries(correccion!.valores)) {
    const c = campo as CampoEditable
    if (c === 'descripcion' || c === 'hscode') {
      ;(l as unknown as Record<string, unknown>)[c] = String(valor ?? '')
      continue
    }
    const texto = String(valor ?? '').trim().replace(',', '.')
    ;(l as unknown as Record<string, unknown>)[c] = texto === '' ? null : Number(texto)
  }

  // 2. La cascada. Un campo fijado no se toca, y no se calcula nada cuyas
  //    entradas no se conozcan: inventar un cero ahí sería peor que el hueco.
  if (!fijados.has('cantidadCajas') && l.unidades && l.unidadesCaja) {
    l.cantidadCajas = Math.ceil(l.unidades / l.unidadesCaja)
  }

  // Los totales de control se recalculan para que el bloque no contradiga a la
  // tabla: son la explicación del dato, y una explicación desfasada engaña.
  if (l.cantidadCajas != null) {
    if (l.pesoCaja != null) l.pesoNetoTotal = dec2(l.pesoCaja * l.cantidadCajas)
    if (l.pesoBrutoCaja != null) l.pesoBrutoTotal = dec2(l.pesoBrutoCaja * l.cantidadCajas)
  }

  return l
}

/** Las líneas finales: correcciones aplicadas y excluidas fuera. */
export function lineasFinales(
  originales: PackingListLinea[],
  correcciones: Record<string, CorreccionLinea>,
): PackingListLinea[] {
  return originales
    .filter(l => !correcciones[claveLinea(l)]?.excluida)
    .map(l => aplicarCorreccion(l, correcciones[claveLinea(l)]))
}

/** ¿Qué campos difieren del original? Para marcarlos en pantalla: un valor
 *  corregido a mano y uno del sistema son idénticos a la vista. */
export function camposCorregidos(
  original: PackingListLinea,
  final: PackingListLinea,
): Set<CampoEditable> {
  const salida = new Set<CampoEditable>()
  for (const campo of CAMPOS_EDITABLES) {
    const a = original[campo] as string | number | null | undefined
    const b = final[campo] as string | number | null | undefined
    if (a == null && b == null) continue
    if (typeof a === 'number' || typeof b === 'number') {
      // Dos decimales, los que se pintan y se imprimen: por debajo no hay cambio.
      if (Math.abs(Number(a ?? 0) - Number(b ?? 0)) >= 0.005) salida.add(campo)
    } else if (String(a ?? '') !== String(b ?? '')) {
      salida.add(campo)
    }
  }
  return salida
}

export interface CorreccionEnviada {
  articulo: string
  matriculaPalet?: string | null
  excluida: boolean
  descripcion?: string | null
  hscode?: string | null
  unidades?: number | null
  unidadesCaja?: number | null
  cantidadCajas?: number | null
  pesoCaja?: number | null
  pesoBrutoCaja?: number | null
}

/** Lo que se manda al backend: solo lo corregido, con su clave completa.
 *
 *  Se comparan los valores FINALES contra el original en vez de mandar lo que el
 *  usuario teclee: así los campos que la cascada ha recalculado también viajan
 *  (son parte de la corrección) y los que volvieron a su valor de origen no. */
export function correccionesParaEnviar(
  originales: PackingListLinea[],
  correcciones: Record<string, CorreccionLinea>,
): CorreccionEnviada[] {
  const salida: CorreccionEnviada[] = []

  for (const original of originales) {
    const c = correcciones[claveLinea(original)]
    if (!c) continue

    const base = { articulo: original.articulo, matriculaPalet: original.matriculaPalet ?? null }
    if (c.excluida) {
      salida.push({ ...base, excluida: true })
      continue
    }

    const final = aplicarCorreccion(original, c)
    const campos = camposCorregidos(original, final)
    if (campos.size === 0) continue

    const envio: CorreccionEnviada = { ...base, excluida: false }
    for (const campo of campos) {
      ;(envio as unknown as Record<string, unknown>)[campo] = final[campo] ?? null
    }
    salida.push(envio)
  }

  return salida
}

// ── Cascada AÉREA: la corrección de una línea sube por los tres niveles ──────
//
// Espejo de `_recalcular_niveles_aereos` del backend, que es la autoridad. Vive
// aquí porque la pantalla tiene que enseñar el efecto mientras el usuario teclea:
// si la banda de indicadores siguiera dando el peso que midió la báscula mientras
// la suma de los bultos dice otra cosa, el documento se contradiría sin que se
// vea.
//
// Solo se aplica cuando HAY correcciones. Recalcular siempre sustituiría los
// pesos medidos por derivados y movería las cifras un céntimo por el redondeo de
// cada bulto (medido: 4.646,08 derivado contra 4.646,07 pesado).

import type { PackingListAirBox, PackingListAirPalet, PackingListResumen } from '@/types'

export interface NivelesAereos {
  resumen: PackingListResumen
  airPalets: PackingListAirPalet[]
  airBoxes: PackingListAirBox[]
}

/** Recalcula air-box → air-palet → carga a partir de las líneas corregidas.
 *
 *  La tara no se toca: es del tipo de palet, no del contenido, así que corregir
 *  una referencia no la cambia. */
export function recalcularNivelesAereos(
  lineas: PackingListLinea[],
  palets: PackingListAirPalet[],
  boxes: PackingListAirBox[],
  resumen: PackingListResumen,
): NivelesAereos {
  const netoPorBox = new Map<string, number>()
  for (const l of lineas) {
    if (!l.matriculaPalet) continue
    const { neto } = pesosLinea(l)
    netoPorBox.set(l.matriculaPalet, (netoPorBox.get(l.matriculaPalet) ?? 0) + neto)
  }

  // Un box sin líneas desaparece: un bulto vacío no viaja, y su peso tampoco
  // puede seguir contando.
  const airBoxes = boxes
    .filter(b => netoPorBox.has(b.airBox))
    .map(b => ({ ...b, peso: dec2(netoPorBox.get(b.airBox)!) }))

  const porPalet = new Map<string, PackingListAirBox[]>()
  for (const b of airBoxes) {
    const arr = porPalet.get(b.airPalet)
    if (arr) arr.push(b); else porPalet.set(b.airPalet, [b])
  }

  const airPalets = palets.flatMap(p => {
    const suyos = porPalet.get(p.airPalet) ?? []
    if (suyos.length === 0) return []
    const neto = dec2(suyos.reduce((s, b) => s + (b.peso ?? 0), 0))
    return [{
      ...p,
      numAirBox: suyos.length,
      pesoNeto: neto,
      pesoBruto: dec2(neto + (p.tara ?? 0)),
    }]
  })

  const netoCarga = dec2(airPalets.reduce((s, p) => s + (p.pesoNeto ?? 0), 0))
  const taraCarga = airPalets.reduce((s, p) => s + (p.tara ?? 0), 0)

  return {
    airPalets,
    airBoxes,
    resumen: {
      ...resumen,
      numAirPalet: airPalets.length,
      numAirBox: airBoxes.length,
      pesoNetoCarga: netoCarga,
      pesoBrutoCarga: dec2(netoCarga + taraCarga),
    },
  }
}

// ── Campos que son del ARTÍCULO, no de la línea ──────────────────────────────

/** Campos que al corregirse se propagan a TODAS las líneas de esa referencia.
 *
 *  Con el grano (matrícula × artículo) el mismo artículo aparece en tantas líneas
 *  como bultos ocupe —en 26APCG000040, 200 veces—, y corregir su código HS bulto
 *  a bulto no tiene ningún sentido: el HS es del artículo, no del palet.
 *
 *  La distinción importa y no es cosmética:
 *
 *  - `descripcion`, `hscode`, `unidadesCaja` salen del maestro de artículo. Son
 *    del artículo SIEMPRE, en marítimo y en aéreo.
 *  - `pesoCaja` y `pesoBrutoCaja` son del artículo en MARÍTIMO (salen del maestro),
 *    pero en AÉREO NO: ahí se prorratean a partir del peso pesado de cada air-box,
 *    así que dos bultos de la misma referencia pueden pesar distinto y propagarlos
 *    machacaría una medida real con la de otro bulto.
 *  - `unidades` y `cantidadCajas` nunca se propagan: son cuántas hay en ESE bulto.
 */
export function camposPropagables(aerea: boolean): CampoEditable[] {
  const delArticulo: CampoEditable[] = ['descripcion', 'hscode', 'unidadesCaja']
  return aerea ? delArticulo : [...delArticulo, 'pesoCaja', 'pesoBrutoCaja']
}

/** Las claves de todas las líneas de la misma referencia. */
export function clavesDelArticulo(
  lineas: PackingListLinea[],
  articulo: string,
): string[] {
  return lineas.filter(l => l.articulo === articulo).map(claveLinea)
}
